import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Printer } from "lucide-react";
import { to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Receipt() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/receipt-view/${token}`).then(({ data }) => setR(data))
      .catch(() => setErr("This receipt link is invalid or no longer available."));
  }, [token]);

  if (err) return <div className="min-h-screen bg-white flex items-center justify-center text-gray-500 text-sm p-6">{err}</div>;
  if (!r) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="animate-spin text-[#B08000]" /></div>;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 print:bg-white print:p-0" data-testid="public-receipt">
      <div className="max-w-2xl mx-auto bg-white shadow-lg print:shadow-none rounded-lg print:rounded-none overflow-hidden text-gray-900">
        <div className="bg-black px-8 py-6 flex items-center justify-between">
          <div>
            <div className="text-[#F2B705] font-black text-2xl tracking-tight">AUTO-HIRE <span className="text-white">STL</span></div>
            <div className="text-white/60 text-xs mt-0.5">Ride With Us · auto-hirestl.com</div>
          </div>
          <div className="text-right">
            <div className="text-white font-bold text-lg">RECEIPT</div>
            <div className="text-white/60 text-xs">{r.receipt_no}</div>
          </div>
        </div>

        <div className="px-8 py-6">
          <div className="grid grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Billed to</div>
              <div className="font-semibold">{r.customer.name}</div>
              {r.customer.email && <div className="text-gray-500 text-xs">{r.customer.email}</div>}
              {r.customer.phone && <div className="text-gray-500 text-xs">{r.customer.phone}</div>}
            </div>
            <div className="text-right">
              <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Rental</div>
              <div className="font-semibold">{r.vehicle}</div>
              <div className="text-gray-500 text-xs">{r.pickup_date} {to12h(r.pickup_time)} → {r.return_date} {to12h(r.return_time)}</div>
              <div className="text-gray-500 text-xs">{r.days} rental day{r.days !== 1 ? "s" : ""} (calendar days)</div>
            </div>
          </div>

          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b-2 border-gray-900 text-left">
                <th className="py-2 font-semibold">Description</th>
                <th className="py-2 font-semibold text-right w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map((l, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{l.label}</td>
                  <td className="py-2 text-right">{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ml-auto max-w-xs text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-gray-500">Total charges</span><b>{money(r.charges_total)}</b></div>
            {r.discount > 0 && <div className="flex justify-between"><span className="text-gray-500">Discounts / credits</span><b className="text-emerald-600">-{money(r.discount)}</b></div>}
            <div className="flex justify-between"><span className="text-gray-500">Amount paid</span><b className="text-emerald-600">-{money(r.amount_paid)}</b></div>
            <div className="flex justify-between border-t-2 border-gray-900 pt-1.5 text-base">
              <span className="font-bold">Balance</span>
              <span className={`font-black ${r.balance > 0.005 ? "text-red-600" : "text-emerald-600"}`}>{money(r.balance)}</span>
            </div>
            {r.balance <= 0.005 && <div className="text-emerald-600 text-xs font-bold text-right">PAID IN FULL — THANK YOU!</div>}
          </div>

          {r.payments?.length > 0 && (
            <div className="mt-6 text-xs">
              <div className="text-gray-400 uppercase tracking-wider mb-1.5">Payment history</div>
              {r.payments.map((p, i) => (
                <div key={i} className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-500">{p.date} · {p.type}</span><span>{money(p.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {r.notes && <div className="mt-6 text-xs text-gray-600 bg-gray-50 rounded p-3 print:bg-white print:border print:border-gray-200">{r.notes}</div>}

          <div className="mt-8 text-center text-xs text-gray-400">
            Thank you for riding with Auto-Hire STL · St. Louis, MO<br />Questions? Visit auto-hirestl.com or reply to your confirmation text.
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-4 text-center print:hidden">
        <button onClick={() => window.print()} data-testid="receipt-print-page-btn"
          className="inline-flex items-center gap-2 bg-black text-[#F2B705] font-bold px-6 py-2.5 rounded-md">
          <Printer size={15} /> Print / Save as PDF
        </button>
      </div>
    </div>
  );
}
